import { useCallback, useMemo, useState } from 'react';
import Layout from '~/components/common/Layout';
import {
  ChakraFlex,
  FAIconName,
  FireflyButton,
  FireflyKebabMenuItem,
} from '@equip.health/ui';
import CareTeamHeaderV2 from '~/components/maud/patients/profile/tabs/careplan/care-team-v2/CareTeamHeaderV2';
import EditCareTeamModal from '~/components/maud/patients/profile/tabs/careplan/care-team-v2/EditCareTeamModal';
import { GlobalErrorCode } from '~/lib/constants/global.constants';
import useErrorToast from '~/lib/hooks/useErrorToast';
import {
  CareTeamMemberCardProps,
  Provider,
} from '~/lib/types/careTeam/careTeam';
import {
  useUpdateProviderTeamAssignmentsMutation,
  useUpdateProviderTeamPreliminaryAssignmentsMutation,
} from '~/store/services/treatmentEpisodes';
import { useGetCareTeam } from '~/lib/hooks/care-team/useGetCareTeam';
import CareTeam from './CareTeam';
import EditReassignmentDateModal from './EditReassignmentDateModal';
import { isProviderActiveForCareTeam } from '~/lib/util/careTeam.util';
import { CareTeamTranslations } from '~/lib/constants/careTeam.constants';
import CareTeamErrorFallback from './CareTeamErrorFallback';
import CareTeamVoidAssignmentsModal from './CareTeamVoidAssignmentsModal';
import { useVoidCareTeamMembers } from '~/lib/hooks/care-team/useVoidCareTeamMembers';
import { useVoidReassignmentMutation } from '~/store/services/clinicalData';
import useHasAuth0Permission from '~/lib/hooks/useHasAuth0Permission';
import { MaudPermissions } from '~/lib/constants/permissions.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { PatientEvents } from '~/analytics/patientEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';

const TOP_LEVEL_ID = 'care-plan__care-team-v2';
const ITEM_WIDTH = 300;

interface CareTeamsProps {
  patientId: string;
}

const CareTeamV2 = ({ patientId }: CareTeamsProps) => {
  const { trackEvent } = useAnalytics();
  const { showErrorToast } = useErrorToast();
  const hasWriteReassignments = useHasAuth0Permission(
    MaudPermissions.MAUD_PROVIDER_REASSIGNMENTS,
  );
  const [
    updateProviderTeamAssignments,
    { isLoading: isUpdatingProviderTeamAssignments },
  ] = useUpdateProviderTeamAssignmentsMutation();
  const [
    updateProviderTeamPreliminaryAssignments,
    { isLoading: isUpdatingProviderTeamPreliminaryAssignments },
  ] = useUpdateProviderTeamPreliminaryAssignmentsMutation();
  const {
    providers,
    isLoading: isCareTeamLoading,
    treatmentEpisodeId,
    inquiryId,
    refetch: refetchCareTeam,
    isError: isCareTeamError,
  } = useGetCareTeam(patientId);

  const [showInactive, setShowInactive] = useState(false);

  const [isEditCareTeamModalOpen, setIsEditCareTeamModalOpen] =
    useState<boolean>(false);

  const [isVoidCareTeamModalOpen, setIsVoidCareTeamModalOpen] =
    useState<boolean>(false);

  const [voidReassignment] = useVoidReassignmentMutation();

  const [editingReassignmentId, setEditingReassignmentId] = useState<
    string | null
  >(null);

  const [updatedCareTeamMembers, setUpdatedCareTeamMembers] = useState<
    CareTeamMemberCardProps[]
  >([]);
  const {
    isLoading: isDeleteProviderTeamAssignmentLoading,
    voidMembersFromCareTeam,
  } = useVoidCareTeamMembers();

  const activeCareTeam = useMemo<CareTeamMemberCardProps[]>(
    () => providers.filter(isProviderActiveForCareTeam),
    [providers],
  );

  const hasInactiveMembers = activeCareTeam.length !== providers.length;

  const toggleVoidCareTeamModal = () => {
    setIsVoidCareTeamModalOpen((prev) => !prev);
    setIsEditCareTeamModalOpen((prev) => !prev);
  };

  const editableCareTeam = useMemo(
    () => activeCareTeam.filter((p) => !p.isOutgoingReassignment),
    [activeCareTeam],
  );

  const toggleEditCareTeamModal = () => {
    setIsEditCareTeamModalOpen((prev) => !prev);
    setUpdatedCareTeamMembers(editableCareTeam);
  };

  const editingReassignment = useMemo(() => {
    if (!editingReassignmentId) return null;
    const departing = providers.find(
      (p) =>
        p.reassignmentId === editingReassignmentId &&
        p.isOutgoingReassignment,
    );
    const incoming = providers.find(
      (p) =>
        p.reassignmentId === editingReassignmentId &&
        p.isIncomingReassignment,
    );
    return {
      currentDate: departing?.reassignmentDate ?? incoming?.reassignmentDate,
      departingProvider: departing,
      incomingProvider: incoming,
    };
  }, [editingReassignmentId, providers]);

  const handleReassignmentDateSaved = () => {
    refetchCareTeam();
  };

  const handleVoidReassignment = useCallback(
    async (reassignmentId: string) => {
      try {
        await voidReassignment(reassignmentId).unwrap();
        trackEvent(PatientEvents.reassignmentVoidSubmission, {
          patientId,
          source: AnalyticsSourceValues.CARE_PLAN_TAB,
        });
        refetchCareTeam();
      } catch (error) {
        showErrorToast(
          GlobalErrorCode.UPDATE_CARE_TEAM,
          error.status ?? 'Unknown error',
          error.data?.message ?? 'Failed to void reassignment',
        );
      }
    },
    [voidReassignment, refetchCareTeam, showErrorToast, trackEvent, patientId],
  );

  const getKebabMenuOptions = useCallback(
    (provider: Provider): FireflyKebabMenuItem[] | undefined => {
      if (
        !hasWriteReassignments ||
        !provider.isIncomingReassignment ||
        !provider.reassignmentId
      )
        return undefined;

      const { reassignmentId } = provider;
      const options: FireflyKebabMenuItem[] = [];

      if (provider.reassignmentDate) {
        options.push({
          label: CareTeamTranslations.EDIT_REASSIGNMENT_DATE,
          leftIcon: FAIconName.calendarLinesRegular,
          onClick: () => setEditingReassignmentId(reassignmentId),
        });
      }

      options.push({
        label: CareTeamTranslations.VOID_REASSIGNMENT,
        leftIcon: FAIconName.trashRegular,
        leftIconColor: 'critical.base',
        onClick: () => {
          trackEvent(PatientEvents.reassignmentVoidClick, {
            patientId,
            source: AnalyticsSourceValues.CARE_PLAN_TAB,
          });
          handleVoidReassignment(reassignmentId);
        },
      });

      return options;
    },
    [hasWriteReassignments, handleVoidReassignment, patientId, trackEvent],
  );

  // Handles confirming edits to the care team, including voiding members if needed
  const onEditCareTeamModalConfirm = async (
    isVoidModalOpen = false,
    overrideCareTeamMembers: CareTeamMemberCardProps[] | null = null,
  ) => {
    const careTeamMembers = overrideCareTeamMembers || updatedCareTeamMembers;

    const voidedCareTeamMembers = careTeamMembers.filter(
      (provider) => provider.isVoidAssignment,
    );

    if (voidedCareTeamMembers.length > 0 && isVoidModalOpen === false) {
      toggleVoidCareTeamModal();
      return;
    }

    const outgoingReassignmentMembers = activeCareTeam
      .filter((p) => p.isOutgoingReassignment)
      .map((provider) => ({
        providerId: provider.id,
        role: provider.role,
        temporary: provider.isTemporary || false,
      }));

    const selectedMembers = [
      ...careTeamMembers
        .filter(
          (provider) => !provider.isEndAssignment && !provider.isVoidAssignment,
        )
        .map((provider) => ({
          providerId: provider.id,
          role: provider.role,
          temporary: provider.isTemporary || false,
        })),
      ...outgoingReassignmentMembers,
    ];

    try {
      if (voidedCareTeamMembers.length > 0) {
        await voidMembersFromCareTeam(
          treatmentEpisodeId,
          voidedCareTeamMembers,
          patientId,
          inquiryId,
          true,
        );
      }
      if (treatmentEpisodeId) {
        await updateProviderTeamAssignments({
          inquiryId,
          patientId,
          providerTeamAssignments: selectedMembers,
          treatmentEpisodeId,
        }).unwrap();
      } else if (inquiryId) {
        await updateProviderTeamPreliminaryAssignments({
          inquiryId,
          patientId,
          providerTeamAssignments: selectedMembers,
        }).unwrap();
      }
    } catch (error) {
      showErrorToast(
        GlobalErrorCode.UPDATE_CARE_TEAM,
        error.status ?? 'Unknown error',
        error.data?.message ?? 'Failed to update care team',
      );
    }
    setIsEditCareTeamModalOpen(false);
    setIsVoidCareTeamModalOpen(false);
  };

  const onVoidCareTeamModalConfirm = async (
    voidCareMembersWithVoidReasons: CareTeamMemberCardProps[],
  ) => {
    const nonVoidUsers = updatedCareTeamMembers.filter(
      (member) => !member.isVoidAssignment,
    );
    const combinedCareTeamMembers = [
      ...voidCareMembersWithVoidReasons,
      ...nonVoidUsers,
    ];
    setUpdatedCareTeamMembers(combinedCareTeamMembers);
    await onEditCareTeamModalConfirm(true, combinedCareTeamMembers);
  };

  const isLoading =
    isCareTeamLoading ||
    isUpdatingProviderTeamAssignments ||
    isUpdatingProviderTeamPreliminaryAssignments ||
    isDeleteProviderTeamAssignmentLoading;

  const hasTreatmentEpisode = !!treatmentEpisodeId;
  const hasNoInquiryOrTreatmentEpisode = !hasTreatmentEpisode && !inquiryId;
  if (isCareTeamError) {
    return <CareTeamErrorFallback />;
  }

  return (
    <Layout minWidth={`${ITEM_WIDTH}px`} title={null} titleId={TOP_LEVEL_ID}>
      <ChakraFlex marginBottom="18px" width="100%">
        <CareTeamHeaderV2
          count={activeCareTeam.length}
          id={`${TOP_LEVEL_ID}-header`}
          isDisabled={isCareTeamLoading || hasNoInquiryOrTreatmentEpisode}
          isLoading={isLoading}
          onEdit={toggleEditCareTeamModal}
          patientId={patientId}
        />
      </ChakraFlex>
      <CareTeam
        getKebabMenuOptions={getKebabMenuOptions}
        isLoading={isCareTeamLoading}
        providers={showInactive ? providers : activeCareTeam}
        {...(hasTreatmentEpisode ? { showTags: true } : {})}
        hasNoInquiryOrTreatmentEpisode={hasNoInquiryOrTreatmentEpisode}
        hasTreatmentEpisode={hasTreatmentEpisode}
        showTextContent
      />
      {hasInactiveMembers && (
        <FireflyButton
          id={`${TOP_LEVEL_ID}__toggle-inactive`}
          variant="tertiary"
          onClick={() => setShowInactive((prev) => !prev)}
          leftIcon={
            showInactive
              ? FAIconName.chevronUpRegular
              : FAIconName.chevronDownRegular
          }
        >
          {showInactive ? 'Hide inactive providers' : 'Show inactive providers'}
        </FireflyButton>
      )}
      {isEditCareTeamModalOpen && (
        <EditCareTeamModal
          careTeamMembers={updatedCareTeamMembers}
          initialCareTeamMembers={editableCareTeam}
          isLoading={isLoading}
          {...(hasTreatmentEpisode ? { hasTreatmentEpisode: true } : {})}
          isOpen={isEditCareTeamModalOpen}
          onCancel={toggleEditCareTeamModal}
          onConfirm={onEditCareTeamModalConfirm}
          patientId={patientId}
          setCareTeamMembers={setUpdatedCareTeamMembers}
        />
      )}
      {isVoidCareTeamModalOpen && (
        <CareTeamVoidAssignmentsModal
          careTeamMembers={updatedCareTeamMembers}
          isLoading={isLoading}
          isOpen={isVoidCareTeamModalOpen}
          onCancel={toggleVoidCareTeamModal}
          onConfirm={onVoidCareTeamModalConfirm}
          patientId={patientId}
        />
      )}
      {editingReassignmentId &&
        editingReassignment?.departingProvider &&
        editingReassignment?.incomingProvider &&
        editingReassignment?.currentDate && (
          <EditReassignmentDateModal
            currentDate={editingReassignment.currentDate}
            departingProvider={editingReassignment.departingProvider}
            incomingProvider={editingReassignment.incomingProvider}
            onClose={() => setEditingReassignmentId(null)}
            onSaveSuccess={handleReassignmentDateSaved}
            patientId={patientId}
            reassignmentId={editingReassignmentId}
          />
        )}
    </Layout>
  );
};

export default CareTeamV2;
